import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, Search, Sparkles, Loader2, Upload, Download, FileText, CheckCircle2, XCircle, Filter, X, ArrowUpDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Combobox } from '../components/ui/combobox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'topic_az', label: 'Topic A–Z' },
  { value: 'topic_za', label: 'Topic Z–A' },
];

export default function AdminQuestions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [allSubtopics, setAllSubtopics] = useState([]);
  const [suggestions, setSuggestions] = useState({ topics: [], subtopics: [] });
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page') || '1'),
    pageSize: 50,
    total: 0,
    totalPages: 0
  });
  
  // Multi-select filters (persisted via URL)
  const [selectedTopics, setSelectedTopics] = useState(() => {
    const t = searchParams.get('topics');
    return t ? t.split(',') : [];
  });
  const [selectedSubtopics, setSelectedSubtopics] = useState(() => {
    const s = searchParams.get('subtopics');
    return s ? s.split(',') : [];
  });
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const st = searchParams.get('statuses');
    return st ? st.split(',') : [];
  });
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort') || 'newest');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAIMode, setIsAIMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [forceDraft, setForceDraft] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, question: null });
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    topic_id: '',
    subtopic: '',
    question_type: 'multiple_choice',
    question_text: '',
    options: ['', '', '', ''],
    correct_option_index: 0,
    correct_answer: '',
    rationale: '',
    source_reference: '',
    difficulty_level: 'intermediate',
    status: 'draft'
  });
  const [aiFormData, setAiFormData] = useState({
    topic: '',
    subtopic: '',
    question_type: 'multiple_choice',
    difficulty_level: 'intermediate',
    guideline: ''
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Persist filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTopics.length) params.set('topics', selectedTopics.join(','));
    if (selectedSubtopics.length) params.set('subtopics', selectedSubtopics.join(','));
    if (selectedStatuses.length) params.set('statuses', selectedStatuses.join(','));
    if (sortBy !== 'newest') params.set('sort', sortBy);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (pagination.page > 1) params.set('page', pagination.page.toString());
    setSearchParams(params, { replace: true });
  }, [selectedTopics, selectedSubtopics, selectedStatuses, sortBy, debouncedSearch, pagination.page, setSearchParams]);

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopics, selectedSubtopics, selectedStatuses, sortBy, debouncedSearch, pagination.page]);

  useEffect(() => {
    fetchTopics();
    fetchSuggestions();
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Pass ALL selected topics/subtopics/statuses as comma-separated (union/OR logic)
      if (selectedTopics.length) params.append('topic_id', selectedTopics.join(','));
      if (selectedSubtopics.length) params.append('subtopic', selectedSubtopics.join(','));
      if (selectedStatuses.length) params.append('status', selectedStatuses.join(','));
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('sort', sortBy);
      params.append('page', pagination.page.toString());
      params.append('page_size', pagination.pageSize.toString());
      
      const response = await axios.get(`${BACKEND_URL}/api/questions?${params}`, {
        withCredentials: true
      });
      
      setQuestions(response.data.questions || []);
      if (response.data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.total_pages
        }));
      }
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async () => {
    try {
      const topicsRes = await axios.get(`${BACKEND_URL}/api/topics`, { withCredentials: true });
      setTopics(topicsRes.data);
    } catch (error) {
      console.error('Failed to load topics');
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/topics/all`, {
        withCredentials: true
      });
      setAllTopics(response.data);
    } catch (error) {
      console.error('Failed to load topics');
    }
  };

  const fetchSubtopics = async (topicKey) => {
    if (!topicKey) {
      setAllSubtopics([]);
      return;
    }
    try {
      // Use canonical subtopics endpoint (pagination-independent)
      const response = await axios.get(`${BACKEND_URL}/api/admin/subtopics-for-topic/${encodeURIComponent(topicKey)}`, {
        withCredentials: true
      });
      setAllSubtopics(response.data);
    } catch (error) {
      console.error('Failed to load subtopics');
      setAllSubtopics([]);
    }
  };

  // Canonical subtopics for filter - fetched independently from pagination
  const [canonicalSubtopics, setCanonicalSubtopics] = useState([]);
  
  // Fetch canonical subtopics when selected topics change
  useEffect(() => {
    const fetchCanonicalSubtopics = async () => {
      if (selectedTopics.length === 0) {
        setCanonicalSubtopics([]);
        return;
      }
      
      try {
        // Fetch subtopics for each selected topic and merge
        const allSubtopics = [];
        const seen = new Set();
        
        for (const topicKey of selectedTopics) {
          const response = await axios.get(
            `${BACKEND_URL}/api/admin/subtopics-for-topic/${encodeURIComponent(topicKey)}`,
            { withCredentials: true }
          );
          
          for (const sub of (response.data || [])) {
            const key = sub.slug || sub.subtopic_id;
            if (!seen.has(key)) {
              seen.add(key);
              allSubtopics.push({
                key: sub.slug || sub.subtopic_id,
                display: sub.display_name,
                question_count: sub.question_count || 0
              });
            }
          }
        }
        
        setCanonicalSubtopics(allSubtopics.sort((a, b) => a.display.localeCompare(b.display)));
      } catch (error) {
        console.error('Failed to load canonical subtopics');
        setCanonicalSubtopics([]);
      }
    };
    
    fetchCanonicalSubtopics();
  }, [selectedTopics]);

  // Use canonical subtopics for filter (PAGINATION INDEPENDENT)
  const availableFilterSubtopics = canonicalSubtopics;

  const handleGenerateAI = async () => {
    if (!aiFormData.topic || !aiFormData.subtopic) {
      toast.error('Topic and subtopic are required');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/questions/generate-ai`,
        aiFormData,
        { withCredentials: true }
      );

      setFormData({
        ...formData,
        topic_id: aiFormData.topic.toLowerCase().replace(/\s+/g, '_'),
        subtopic: aiFormData.subtopic,
        question_type: aiFormData.question_type,
        question_text: response.data.question_text,
        options: response.data.options || ['', '', '', ''],
        correct_option_index: response.data.correct_option_index || 0,
        correct_answer: response.data.correct_answer,
        rationale: response.data.rationale,
        source_reference: response.data.source_reference,
        difficulty_level: aiFormData.difficulty_level,
        status: 'draft'
      });

      setIsAIMode(false);
      toast.success('Question generated! Review and save when ready.');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 503) {
        toast.error(detail || 'AI service temporarily unavailable. Please try manual creation.');
      } else if (status === 401 || status === 403) {
        toast.error('Authentication error. Please contact administrator.');
      } else {
        toast.error(detail || 'Failed to generate question. Please try again or use manual creation.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    try {
      if (!formData.question_text.trim()) {
        toast.error('Question text is required');
        return;
      }
      if (!formData.rationale.trim()) {
        toast.error('Rationale is required');
        return;
      }
      if (!formData.source_reference.trim()) {
        toast.error('Source reference is required');
        return;
      }
      
      if (formData.question_type === 'multiple_choice') {
        const validOptions = formData.options.map(o => o.trim()).filter(o => o);
        
        if (validOptions.length < 3) {
          toast.error('Multiple choice questions need at least 3 options');
          return;
        }
        
        const uniqueOptions = new Set(validOptions.map(o => o.toLowerCase()));
        if (uniqueOptions.size !== validOptions.length) {
          toast.error('Options must be unique');
          return;
        }
        
        if (formData.correct_option_index === null || formData.correct_option_index === undefined) {
          toast.error('Please select the correct answer');
          return;
        }
      }
      
      if (formData.question_type === 'fill_in_blank') {
        if (!formData.correct_answer || !formData.correct_answer.trim()) {
          toast.error('Fill-in-blank questions require a correct answer');
          return;
        }
      }

      const payload = {
        ...formData,
        options: formData.question_type === 'multiple_choice' 
          ? formData.options.map(o => o.trim()).filter(o => o)
          : null,
        correct_option_index: formData.question_type === 'multiple_choice' 
          ? formData.correct_option_index 
          : null,
        correct_answer_text: formData.question_type === 'fill_in_blank' 
          ? (formData.correct_answer || '').trim()
          : null
      };

      if (editingQuestion) {
        await axios.put(
          `${BACKEND_URL}/api/questions/${editingQuestion.question_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Question updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/questions`,
          payload,
          { withCredentials: true }
        );
        toast.success('Question created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchQuestions();
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingQuestion ? 'Failed to update question' : 'Failed to create question'));
    }
  };

  const handleDelete = async (questionId) => {
    setIsDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/questions/${questionId}`, {
        withCredentials: true
      });
      
      fetchQuestions();  // Refresh to get updated pagination
      toast.success('Question deleted successfully');
      fetchSuggestions();
      setDeleteConfirm({ open: false, question: null });
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete question';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteConfirm = (question) => {
    setDeleteConfirm({ open: true, question });
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    
    let correctOptionIndex = 0;
    if (question.question_type === 'multiple_choice' && question.options) {
      const correctAnswer = (question.correct_answer || '').toLowerCase().trim();
      if (correctAnswer) {
        correctOptionIndex = question.options.findIndex(opt => 
          (opt || '').toLowerCase().trim() === correctAnswer
        );
      }
      if (correctOptionIndex === -1) correctOptionIndex = 0;
    }
    
    let correctAnswerValue = '';
    if (question.question_type === 'fill_in_blank') {
      correctAnswerValue = question.correct_answer_text || question.correct_answer || '';
    } else {
      correctAnswerValue = question.correct_answer || '';
    }
    
    const topicDisplay = question.topic_display_name || question.topic_id;
    const subtopicDisplay = question.subtopic_display_name || question.subtopic;
    
    setFormData({
      topic_id: topicDisplay,
      subtopic: subtopicDisplay,
      question_type: question.question_type,
      question_text: question.question_text,
      options: question.options || ['', '', '', ''],
      correct_option_index: correctOptionIndex,
      correct_answer: correctAnswerValue,
      rationale: question.rationale || '',
      source_reference: question.source_reference || '',
      difficulty_level: question.difficulty_level,
      status: question.status || 'draft'
    });
    
    if (question.topic_key) {
      fetchSubtopics(question.topic_key);
    }
    
    setIsDialogOpen(true);
    setIsAIMode(false);
  };

  const resetForm = () => {
    setEditingQuestion(null);
    setIsAIMode(false);
    setFormData({
      topic_id: '',
      subtopic: '',
      question_type: 'multiple_choice',
      question_text: '',
      options: ['', '', '', ''],
      correct_option_index: 0,
      correct_answer: '',
      rationale: '',
      source_reference: '',
      difficulty_level: 'intermediate',
      status: 'draft'
    });
    setAiFormData({
      topic: '',
      subtopic: '',
      question_type: 'multiple_choice',
      difficulty_level: 'intermediate',
      guideline: ''
    });
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'topic', 'subtopic', 'question_type', 'difficulty', 'status', 'question_text',
      'option_1', 'option_2', 'option_3', 'option_4', 'correct_answer',
      'correct_answer_text', 'correct_answer_bool', 'rationale', 'source_reference'
    ];
    
    const instructionRow = {
      topic: 'OPTIONAL: Leave blank to default to "Unassigned"',
      subtopic: 'OPTIONAL: Leave blank to default to "General"',
      question_type: 'ACCEPTS: Multiple Choice, MCQ, Fill in the Blank, FIB, True False, T/F',
      difficulty: 'ACCEPTS: Beginner, Intermediate, Advanced. Default: Beginner',
      status: 'ACCEPTS: Draft, Published. Default: Draft',
      question_text: 'REQUIRED: The question text',
      option_1: 'For MCQ only: First option',
      option_2: 'For MCQ only: Second option',
      option_3: 'For MCQ only: Third option (min 3 required)',
      option_4: 'For MCQ only: Fourth option (optional)',
      correct_answer: 'For MCQ: Must match one option exactly. For T/F: true/false/yes/no',
      correct_answer_text: 'For Fill-in-Blank only: Correct answer(s). Use | for multiple',
      correct_answer_bool: 'For T/F only: true/false/yes/no/t/f/1/0',
      rationale: 'OPTIONAL: Explanation of the correct answer',
      source_reference: 'OPTIONAL: Reference source'
    };
    
    const exampleRows = [
      {
        topic: 'Vasopressors', subtopic: 'Norepinephrine', question_type: 'Multiple Choice',
        difficulty: 'Intermediate', status: '', question_text: 'What is the primary mechanism?',
        option_1: 'Alpha-1', option_2: 'Beta-1', option_3: 'Alpha and beta-1', option_4: 'Dopaminergic',
        correct_answer: 'Alpha and beta-1', correct_answer_text: '', correct_answer_bool: '',
        rationale: 'Norepinephrine acts on both alpha and beta-1 receptors',
        source_reference: 'Critical Care Guidelines 2024'
      }
    ];
    
    const escapeCSV = (val) => {
      if (!val) return '';
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };
    
    const csvContent = [
      headers.join(','),
      headers.map(h => escapeCSV(`# ${instructionRow[h] || ''}`)).join(','),
      ...exampleRows.map(row => headers.map(h => escapeCSV(row[h] || '')).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'question_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleImportCSV = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);
      formDataUpload.append('force_draft', forceDraft);

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/questions/import-csv`,
        formDataUpload,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setImportResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Imported ${response.data.imported_count} questions!`);
        fetchQuestions();
        fetchSuggestions();
      }
      
      if (response.data.skipped_count > 0) {
        toast.warning(`${response.data.skipped_count} questions skipped`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setIsImporting(false);
    }
  };

  const clearFilters = () => {
    setSelectedTopics([]);
    setSelectedSubtopics([]);
    setSelectedStatuses([]);
    setSortBy('newest');
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleTopic = (topicKey) => {
    setSelectedTopics(prev => {
      const next = prev.includes(topicKey) ? prev.filter(t => t !== topicKey) : [...prev, topicKey];
      // Clear subtopics if no topics selected
      if (next.length === 0) setSelectedSubtopics([]);
      return next;
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleSubtopic = (subtopicKey) => {
    setSelectedSubtopics(prev => 
      prev.includes(subtopicKey) ? prev.filter(s => s !== subtopicKey) : [...prev, subtopicKey]
    );
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const hasActiveFilters = selectedTopics.length > 0 || selectedSubtopics.length > 0 || selectedStatuses.length > 0 || searchQuery;

  // No client-side filtering needed - server handles it
  // questions array is already filtered and sorted from backend

  if (loading && questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            data-testid="admin-questions-back-button"
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="hover:bg-slate-100 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-slate-900 font-sans">Manage Questions</h1>
            <div className="flex gap-3">
              <Button
                onClick={() => { setSelectedFile(null); setImportResult(null); setForceDraft(true); setIsImportDialogOpen(true); }}
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-50 h-10 px-6 rounded-full font-semibold"
              >
                <Upload className="w-4 h-4 mr-2" /> Import CSV
              </Button>
              <Button
                onClick={() => { resetForm(); setIsAIMode(true); setIsDialogOpen(true); }}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 h-10 px-6 rounded-full font-semibold shadow-lg"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Generate with AI
              </Button>
              <Button
                onClick={() => { resetForm(); setIsDialogOpen(true); }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 rounded-full font-semibold shadow-lg shadow-primary/20"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Question
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="font-semibold text-slate-700">Filters</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 hover:text-slate-700">
                <X className="w-3 h-3 mr-1" /> Clear All
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-slate-400" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {/* Topics Multi-select */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-2 block">Topics</label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {allTopics.map(t => (
                  <button
                    key={t.key}
                    onClick={() => toggleTopic(t.key)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                      selectedTopics.includes(t.key)
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                    }`}
                  >
                    {t.display}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Subtopics Multi-select */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-2 block">Subtopics</label>
              {selectedTopics.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Select a topic first</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {availableFilterSubtopics.map(s => (
                    <button
                      key={s.key}
                      onClick={() => toggleSubtopic(s.key)}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                        selectedSubtopics.includes(s.key)
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                      }`}
                    >
                      {s.display}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Status Multi-select */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-2 block">Status</label>
              <div className="flex flex-wrap gap-1.5">
                {['draft', 'published'].map(status => (
                  <button
                    key={status}
                    onClick={() => toggleStatus(status)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-all capitalize ${
                      selectedStatuses.includes(status)
                        ? status === 'published' ? 'bg-green-500 text-white border-green-500' : 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Search */}
          <div className="mt-4 flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions..."
                className="pl-10"
              />
            </div>
            <span className="text-sm text-slate-500">
              Showing {questions.length} of {pagination.total} questions
            </span>
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-4">
          {questions.map((question) => (
            <div
              key={question.question_id}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-hover transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="mb-2">
                    <span className={`px-3 py-1 text-sm rounded-full font-semibold ${
                      question.topic_key === 'unassigned' || !question.topic_key
                        ? 'bg-orange-100 text-orange-700 border border-orange-200'
                        : 'bg-blue-100 text-blue-700 border border-blue-200'
                    }`}>
                      {question.topic_display_name || 'Unassigned'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-bold tracking-wide uppercase">
                      {question.subtopic_display_name || question.subtopic || 'General'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full font-bold tracking-wide uppercase ${
                      question.difficulty_level === 'beginner' ? 'bg-emerald-100 text-emerald-700' :
                      question.difficulty_level === 'intermediate' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {question.difficulty_level}
                    </span>
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-bold tracking-wide uppercase">
                      {question.question_type.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full font-bold tracking-wide uppercase ${
                      question.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {question.status}
                    </span>
                  </div>
                  <p className="text-slate-900 font-medium mb-2">{question.question_text}</p>
                  <p className="text-sm text-success mb-1"><strong>Answer:</strong> {question.correct_answer}</p>
                  <p className="text-sm text-slate-600"><strong>Source:</strong> {question.source_reference}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button onClick={() => handleEdit(question)} variant="ghost" size="sm" className="hover:bg-slate-100">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => openDeleteConfirm(question)} variant="ghost" size="sm" className="hover:bg-red-100 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {questions.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <p className="text-slate-600">No questions found</p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= pagination.totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* AI Generation Dialog */}
      <Dialog open={isDialogOpen && isAIMode} onOpenChange={(open) => { if (!open) { setIsDialogOpen(false); resetForm(); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Generate Question with AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Topic *</Label>
              <Input
                value={aiFormData.topic}
                onChange={(e) => setAiFormData({...aiFormData, topic: e.target.value})}
                placeholder="e.g., Vasopressors, Sepsis Management"
              />
            </div>
            <div>
              <Label>Subtopic *</Label>
              <Input
                value={aiFormData.subtopic}
                onChange={(e) => setAiFormData({...aiFormData, subtopic: e.target.value})}
                placeholder="e.g., Norepinephrine, Early Recognition"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Question Type</Label>
                <Select value={aiFormData.question_type} onValueChange={(val) => setAiFormData({...aiFormData, question_type: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                    <SelectItem value="fill_in_blank">Fill in the Blank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={aiFormData.difficulty_level} onValueChange={(val) => setAiFormData({...aiFormData, difficulty_level: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Guideline/Source (Optional)</Label>
              <Input
                value={aiFormData.guideline}
                onChange={(e) => setAiFormData({...aiFormData, guideline: e.target.value})}
                placeholder="e.g., ACC/AHA Guidelines"
              />
            </div>
            <Button onClick={handleGenerateAI} disabled={isGenerating} className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white h-12 rounded-full font-semibold">
              {isGenerating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Generate Question</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Create/Edit Dialog */}
      <Dialog open={isDialogOpen && !isAIMode} onOpenChange={(open) => { if (!open) { setIsDialogOpen(false); resetForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Create New Question'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Topic *</Label>
                <Combobox
                  options={allTopics.map(t => ({ value: t.display, label: t.display, key: t.key }))}
                  value={formData.topic_id}
                  onValueChange={(val) => {
                    setFormData({...formData, topic_id: val, subtopic: ''});
                    const selectedTopic = allTopics.find(t => t.display === val);
                    const topicKey = selectedTopic?.key || (val ? val.toLowerCase().replace(/\s+/g, '_') : '');
                    fetchSubtopics(topicKey);
                  }}
                  placeholder="Select or type to create..."
                  searchPlaceholder="Search topics..."
                  allowCreate={true}
                  onCreateNew={() => { setAllSubtopics([]); toast.success('New topic will be created when you save'); }}
                  className="w-full"
                />
              </div>
              <div>
                <Label>Subtopic *</Label>
                <Combobox
                  options={allSubtopics.map(s => ({ value: s.display, label: s.display, key: s.key }))}
                  value={formData.subtopic}
                  onValueChange={(val) => setFormData({...formData, subtopic: val})}
                  placeholder="Select or type to create..."
                  searchPlaceholder="Search subtopics..."
                  allowCreate={true}
                  onCreateNew={() => toast.success('New subtopic will be created when you save')}
                  disabled={!formData.topic_id}
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Question Type</Label>
                <Select value={formData.question_type} onValueChange={(val) => setFormData({...formData, question_type: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                    <SelectItem value="fill_in_blank">Fill in the Blank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={formData.difficulty_level} onValueChange={(val) => setFormData({...formData, difficulty_level: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Question Text *</Label>
              <Textarea value={formData.question_text} onChange={(e) => setFormData({...formData, question_text: e.target.value})} placeholder="Enter the question..." rows={3} />
            </div>

            {formData.question_type === 'multiple_choice' && (
              <div>
                <Label>Options (min 3) *</Label>
                {formData.options.map((option, idx) => (
                  <Input key={idx} value={option} onChange={(e) => { const newOptions = [...formData.options]; newOptions[idx] = e.target.value; setFormData({...formData, options: newOptions}); }} placeholder={`Option ${idx + 1}${idx < 3 ? ' (required)' : ''}`} className="mb-2" />
                ))}
              </div>
            )}

            {formData.question_type === 'multiple_choice' ? (
              <div>
                <Label>Correct Answer *</Label>
                <Select value={String(formData.correct_option_index)} onValueChange={(val) => setFormData({...formData, correct_option_index: parseInt(val)})}>
                  <SelectTrigger><SelectValue placeholder="Select the correct option" /></SelectTrigger>
                  <SelectContent>
                    {formData.options.map((option, idx) => (option.trim() && <SelectItem key={idx} value={String(idx)}>Option {idx + 1}: {option.trim().substring(0, 50)}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Correct Answer *</Label>
                <Input value={formData.correct_answer} onChange={(e) => setFormData({...formData, correct_answer: e.target.value})} placeholder="Enter the correct answer..." />
              </div>
            )}

            <div>
              <Label>Rationale *</Label>
              <Textarea value={formData.rationale} onChange={(e) => setFormData({...formData, rationale: e.target.value})} placeholder="Explain why this is the correct answer..." rows={4} />
            </div>

            <div>
              <Label>Source Reference *</Label>
              <Input value={formData.source_reference} onChange={(e) => setFormData({...formData, source_reference: e.target.value})} placeholder="e.g., Surviving Sepsis Campaign Guidelines 2021" />
            </div>

            <Button onClick={handleCreateOrUpdate} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full font-semibold">
              {editingQuestion ? 'Update Question' : 'Create Question'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-primary" /> Import Questions from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {!importResult ? (
              <>
                <div>
                  <Label>CSV File *</Label>
                  <Input type="file" accept=".csv" onChange={(e) => setSelectedFile(e.target.files[0])} className="mt-2" />
                </div>
                <div className="flex items-center space-x-2 bg-slate-50 p-4 rounded-lg">
                  <Checkbox id="force-draft" checked={forceDraft} onCheckedChange={setForceDraft} />
                  <label htmlFor="force-draft" className="text-sm font-medium cursor-pointer">Force all imported questions to Draft status (recommended)</label>
                </div>
                <div className="border-t pt-4">
                  <Button onClick={handleDownloadTemplate} variant="outline" className="w-full border-slate-200 text-slate-700 hover:bg-slate-50">
                    <Download className="w-4 h-4 mr-2" /> Download CSV Template with Examples
                  </Button>
                </div>
                <Button onClick={handleImportCSV} disabled={!selectedFile || isImporting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full font-semibold">
                  {isImporting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>) : (<><Upload className="w-4 h-4 mr-2" /> Import Questions</>)}
                </Button>
              </>
            ) : (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-green-700">{importResult.imported_count}</div>
                    <div className="text-sm text-green-600">Imported</div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <XCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-yellow-700">{importResult.skipped_count}</div>
                    <div className="text-sm text-yellow-600">Skipped</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-blue-700">{importResult.imported_count + importResult.skipped_count}</div>
                    <div className="text-sm text-blue-600">Total</div>
                  </div>
                </div>
                <Button onClick={() => { setIsImportDialogOpen(false); setImportResult(null); setSelectedFile(null); }} className="w-full mt-4 bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full font-semibold">Close</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !isDeleting && setDeleteConfirm({ open, question: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Delete Question</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">Are you sure you want to delete this question? This action cannot be undone.</p>
            {deleteConfirm.question && (
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-sm font-medium text-slate-900 mb-2">{deleteConfirm.question.question_text?.substring(0, 100)}...</p>
                <div className="flex gap-2 flex-wrap">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">{deleteConfirm.question.topic_display_name || 'Unassigned'}</span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">{deleteConfirm.question.subtopic_display_name || deleteConfirm.question.subtopic}</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, question: null })} disabled={isDeleting}>Cancel</Button>
            <Button onClick={() => deleteConfirm.question && handleDelete(deleteConfirm.question.question_id)} disabled={isDeleting} className="bg-red-600 text-white hover:bg-red-700">
              {isDeleting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>) : (<><Trash2 className="w-4 h-4 mr-2" /> Delete</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
